import dayjs from 'dayjs'
import { useFieldValues } from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import { useDrilldownSchema } from 'features/drilldown/rolodex/DrilldownDataSchema'
import { DrilldownFieldType } from 'features/drilldown/rolodex/ontology/ontologyTypes'
import { useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

// Shared styled components
export const EmptyValue = styled.span`
  color: ${CantileverTheme.colors.gray['400']};
  font-style: italic;
`

export const ErrorText = styled.div`
  color: ${CantileverTheme.colors.error['500']};
  font-size: 12px;
  margin-top: 4px;
  font-style: italic;
`

export const parseAndValidateDate = (value: string): dayjs.Dayjs | null => {
  if (!value) return null
  try {
    const dateFromExpectedFormat = dayjs(value, 'YYYY-MM-DD', true)
    if (dateFromExpectedFormat.isValid()) {
      return dateFromExpectedFormat
    }

    const otherParsedDate = dayjs(value)
    return otherParsedDate.isValid() ? otherParsedDate : null
  } catch {
    return null
  }
}

/**
 * Converts any value to a display string for input components
 */
export const convertValueToDisplayString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  return String(value)
}

/**
 * Formats a value for display based on its field type
 */
export const formatValueForDisplay = (
  value: unknown,
  type: DrilldownFieldType
): string => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  switch (type) {
    case 'boolean':
      return String(value).toLowerCase() === 'true' ? 'True' : 'False'

    case 'date':
      try {
        const parsedDate = parseAndValidateDate(String(value))
        if (parsedDate) {
          return parsedDate.format('YYYY-MM-DD')
        }
      } catch {
        // return as string if parsing fails
      }
      return String(value)

    case 'object':
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value, null, 2)
      }
      return convertValueToDisplayString(value)

    case 'array':
      if (Array.isArray(value)) {
        return JSON.stringify(value, null, 2)
      }
      return convertValueToDisplayString(value)

    default:
      return convertValueToDisplayString(value)
  }
}

const DEFAULT_PLACEHOLDERS: Record<DrilldownFieldType, string> = {
  text: 'Enter text value',
  number: 'Enter number value',
  boolean: 'Select boolean value',
  date: 'Select date',
  object: 'Enter valid JSON object',
  array: 'Enter valid JSON array',
  enum: 'Select or type enum value',
} as const

/**
 * Gets the default placeholder for a field type
 */
export const getDefaultPlaceholder = (type: DrilldownFieldType): string => {
  return DEFAULT_PLACEHOLDERS[type] || 'Enter value'
}

/**
 * Checks if a value is valid for a given field type
 */
export const isValidValueForType = (
  value: unknown,
  type: DrilldownFieldType
): boolean => {
  if (value === null || value === undefined || value === '') {
    return true
  }

  switch (type) {
    case 'number':
      return !isNaN(Number(value))

    case 'boolean': {
      const stringValue = String(value).toLowerCase()
      return stringValue === 'true' || stringValue === 'false'
    }

    case 'date': {
      const parsedDate = parseAndValidateDate(String(value))
      return parsedDate !== null
    }

    case 'object':
      try {
        const parsed = JSON.parse(String(value))
        return (
          typeof parsed === 'object' &&
          parsed !== null &&
          !Array.isArray(parsed)
        )
      } catch {
        return false
      }

    case 'array':
      try {
        const parsed = JSON.parse(String(value))
        return Array.isArray(parsed)
      } catch {
        return false
      }

    default:
      return true
  }
}

/**
 * Hook for handling enum field values and options
 * Load the existing values set in the ontology as opposed to those allowed by the ontology.
 * Long-term, we will likely move off this and onto just ontology values.
 */
export const usePreexistingEnumSelectOptions = (
  type: DrilldownFieldType,
  fieldKey?: string
) => {
  const { values: enumValues, isLoading: isLoadingEnumValues } = useFieldValues(
    fieldKey || ''
  )

  const enumOptions = useMemo(() => {
    if (type !== 'enum' || !fieldKey) return []

    return enumValues.map((val) => ({
      value: val,
      label: val,
    }))
  }, [type, fieldKey, enumValues])

  return {
    enumOptions,
    isLoadingEnumValues,
  }
}

/**
 * Hook for handling enum field values and their options
 * TODO move this to generic ontology once it's split out into a feature
 *
 * @param fieldKey the field key '{type}.{field}' to get the enum values for
 * @returns the allowed enum value options.
 */
export const useOntologyEnumSelectOptions = (
  type: DrilldownFieldType,
  fieldKey: string
) => {
  const { fields } = useDrilldownSchema()

  return useMemo(() => {
    if (type !== 'enum') {
      return []
    }

    const field = fields[fieldKey]
    const enumValues = field?.allowedValues || []

    if (!enumValues) {
      return []
    }

    return enumValues.map((val) => ({
      value: val,
      label: val,
    }))
  }, [fieldKey, fields, type])
}
